import ApplicationController from './application_controller'
import { missingAddressComponents } from '../lib/address_components'

export default class extends ApplicationController {
  static values = {
    step: String,
    url: String
  }

  connect () {
    this.handleInvalid = this.handleInvalid.bind(this)
    this.handlePlaceChanged = this.handlePlaceChanged.bind(this)

    this.element.addEventListener('invalid', this.handleInvalid, true)
    document.addEventListener('place_autocomplete.delivery', this.handlePlaceChanged)
    document.addEventListener('place_autocomplete.billing', this.handlePlaceChanged)
    document.addEventListener('place_autocomplete.picking', this.handlePlaceChanged)
  }

  disconnect () {
    this.element.removeEventListener('invalid', this.handleInvalid, true)
    document.removeEventListener('place_autocomplete.delivery', this.handlePlaceChanged)
    document.removeEventListener('place_autocomplete.billing', this.handlePlaceChanged)
    document.removeEventListener('place_autocomplete.picking', this.handlePlaceChanged)
  }

  handleInvalid (event) {
    const field = event.target

    this.track({
      step: this.stepForField(field),
      field_name: this.fieldName(field),
      user_action: 'browser_validation',
      error_type: field.validity?.valueMissing ? 'missing_required_data' : 'client_side_blocking',
      error_message: this.validationMessage(field),
      explanation: 'The user was blocked in the browser before the widget form could be submitted.',
      blocking: true,
      source: 'customer_ui',
      metadata: {
        input_type: field.type,
        validity: this.validityState(field)
      }
    })
  }

  handlePlaceChanged (event) {
    const role = event.type.split('.').pop()
    const place = event.detail?.place
    const missing = missingAddressComponents(place)

    if (missing.length === 0) return

    this.track({
      step: this.stepForAddressRole(role),
      field_name: `${role}_address.raw_address`,
      user_action: `select_${role}_autocomplete_address`,
      error_type: 'autocomplete_failure',
      error_message: `Autocomplete address is missing ${missing.join(', ')}`,
      explanation: 'The customer selected an autocomplete address, but Google Places did not return all address details required by the widget.',
      blocking: false,
      source: 'autocomplete',
      metadata: {
        missing_components: missing,
        formatted_address: place?.formatted_address
      }
    })
  }

  stepForAddressRole (role) {
    if (role === 'billing') return 'billing_address'
    if (role === 'picking') return 'picking_address'

    return 'delivery_address'
  }

  track (payload) {
    if (!this.hasUrlValue) return

    fetch(this.urlValue, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfToken()
      },
      body: JSON.stringify({ widget_error_event: payload }),
      credentials: 'same-origin',
      keepalive: true
    }).catch(() => {})
  }

  csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  fieldName (field) {
    return this.normalizeFieldName(field.name || field.id || field.dataset?.fieldName || 'unknown')
  }

  normalizeFieldName (name) {
    // Keep this aligned with ProductWidgets::ErrorTracker.normalize_field_name.
    return name
      .replace(/_attributes/g, '')
      .replace(/\]\[/g, '.')
      .replace(/[\][]/g, '')
      .replace(/^lead\./, '')
      .replace(/\//g, '.')
  }

  stepForField (field) {
    const name = this.fieldName(field)

    if (name.includes('billing')) return 'billing_address'
    if (name.includes('delivery')) return 'delivery_address'
    if (name.includes('picking')) return 'picking_address'
    if (name.includes('customer')) return 'customer_information'

    return this.hasStepValue ? this.stepValue : 'product_widget'
  }

  validityState (field) {
    if (!field.validity) return {}

    return {
      value_missing: field.validity.valueMissing,
      type_mismatch: field.validity.typeMismatch,
      pattern_mismatch: field.validity.patternMismatch,
      too_short: field.validity.tooShort,
      too_long: field.validity.tooLong,
      range_underflow: field.validity.rangeUnderflow,
      range_overflow: field.validity.rangeOverflow,
      step_mismatch: field.validity.stepMismatch,
      bad_input: field.validity.badInput,
      custom_error: field.validity.customError
    }
  }

  validationMessage (field) {
    const message = field.validationMessage || 'Browser validation blocked the field'
    const value = field.value

    if (!value || value.length < 3) return message

    return message.replaceAll(value, '[FILTERED]')
  }
}
